import { traducirError, useTraduccion } from '@tallerya/i18n';
import {
  Boton,
  CampoAreaTexto,
  CampoCorreo,
  CampoDocumento,
  CampoInterruptor,
  CampoSegmentado,
  CampoSeleccion,
  CampoTelefono,
  CampoTexto,
  esCorreo,
  esTelefono,
  Fila,
  Formulario,
  guiarTelefono,
  IconoChevronAbajo,
  IconoMas,
  Seccion,
  useAvisos,
  useFormulario,
  type ErroresValidacion,
  type EstadoFormulario,
} from '@tallerya/ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { EliminarCliente } from './EliminarCliente';
import { encabezado } from './encabezado';
import {
  pedirSiguienteCodigo,
  useCambiosCliente,
  useFichaCliente,
  useSiguienteCodigo,
  useTiposDocumento,
  version,
  type ClienteExistente,
  type ContactoFila,
  type FichaCliente,
  type ValoresCliente,
} from './datos';

// Alta y edición de un cliente (#104): «Clientes - Agregar» (Persona, Empresa,
// Errores) y «Clientes - Editar» del Figma. La misma pantalla para las dos, como
// en sucursales; lo que cambia:
//
//   /clientes/nuevo          alta: teléfono y correo sueltos, sin «Estado»
//   /clientes/:id/editar     edición: la tabla de contactos y el interruptor
//
// El alta se tiene que poder hacer con la clase y el nombre y nada más: es lo
// que se captura en el mostrador con el carro entrando. Ningún otro campo es
// obligatorio.

/**
 * Los topes de la API (#176), para el contador del campo y para que el error diga
 * cuánto cabe. Si la API cambia uno, cambia acá.
 */
const MAXIMO = {
  /** `Customer.CodeMaxLength` */
  codigo: 50,
  /** `Customer.NameMaxLength`, también del nombre comercial. */
  nombre: 250,
  /** `Customer.DocIdentifierMaxLength` */
  documento: 50,
  /** `CustomerContact.PhoneMaxLength` */
  telefono: 50,
  /** `CustomerContact.EmailMaxLength` */
  correo: 254,
  /** `CustomerContact.AddressMaxLength` */
  direccion: 500,
  /** `CustomerContact.LabelMaxLength` */
  etiqueta: 250,
  /** `Customer.NotesMaxLength` */
  notas: 2500,
} as const;

/** El tope del valor de un contacto de la tabla, según su tipo. */
const maximoDe = (tipo: ContactoFila['kind']) => (tipo === 'phone' ? MAXIMO.telefono : MAXIMO.correo);

/** El alta en blanco. El código lo pone la propuesta de `next-code`. */
const ALTA: ValoresCliente = {
  kind: 'person',
  code: '',
  name: '',
  tradeName: '',
  docTypeId: '',
  docIdentifier: '',
  address: '',
  notes: '',
  phone: '',
  email: '',
  contacts: [],
  isActive: true,
};

export function FormularioCliente() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const alta = id === undefined;
  const ficha = useFichaCliente(id);
  const siguiente = useSiguienteCodigo(alta);
  useTituloPantalla(alta ? t('admin.clientes.nuevo') : t('admin.clientes.editar.titulo'));

  if (alta) {
    // Se espera la propuesta antes de dibujar, para que el formulario arranque
    // con ella y no haya que escribirla encima de lo que la persona ya tecleó. Si
    // falla, el alta sigue con el código vacío: no se bloquea por eso.
    if (siguiente.isPending) return <p className="entrada">{t('app.cargando')}</p>;

    return <Editor key="nuevo" codigoPropuesto={siguiente.data ?? ''} />;
  }

  if (ficha.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (ficha.isError || ficha.data === null) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.clientes.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.clientes.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/clientes')}>
          {t('admin.clientes.volver')}
        </Boton>
      </section>
    );
  }

  // La clave lleva la versión: si la ficha cambia en la base y se vuelve a pedir,
  // el formulario arranca de nuevo con los valores frescos.
  return <Editor key={`${ficha.data.id}-${ficha.data.version}`} ficha={ficha.data} />;
}

interface PropsEditor {
  ficha?: FichaCliente;
  /** Solo el alta: el código que tendría el próximo cliente, o vacío si no se pudo saber. */
  codigoPropuesto?: string;
}

function Editor({ ficha, codigoPropuesto = '' }: PropsEditor) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  // Desde el listado o la ficha se vuelve con «Atrás»: el listado regresa con sus
  // filtros en la URL. Quien entró por un enlace no tiene nada detrás, y va al
  // listado limpio. Con `desdeFicha`, `desdeListado` dice si el listado está
  // detrás de la ficha.
  const origen = useLocation().state as { desdeListado?: boolean; desdeFicha?: boolean } | null;
  const volver = () => void (origen?.desdeListado || origen?.desdeFicha ? navegar(-1) : navegar('/clientes'));
  // Eliminado, la ficha ya no existe: se salta hasta el listado que hay detrás, o
  // se reemplaza esta pantalla por uno limpio.
  const alEliminar = () =>
    void (origen?.desdeFicha
      ? origen.desdeListado
        ? navegar(-2)
        : navegar('/clientes', { replace: true })
      : volver());
  const { avisar } = useAvisos();
  const cambios = useCambiosCliente();
  const [eliminando, setEliminando] = useState(false);
  // El código que se mostró ya puesto. Si al guardar sigue igual, nadie lo tocó.
  const [propuesto, setPropuesto] = useState(codigoPropuesto);
  // El cliente que ya tiene el documento, para «Ver cliente existente →».
  const [existente, setExistente] = useState<ClienteExistente | null>(null);
  const editando = ficha !== undefined;
  // «Guardar y desactivar» desde la ventana de eliminar (#177): el mismo guardado
  // de siempre, con el cliente inactivo. Va en un ref porque `enviar` toma los
  // valores del último dibujado: cambiar el interruptor y enviar en el mismo paso
  // mandaría el valor de antes.
  const desactivarAlGuardar = useRef(false);

  const form = useFormulario<ValoresCliente>({
    inicial: ficha ? valoresDeFicha(ficha) : { ...ALTA, code: codigoPropuesto },
    // La cortesía de no ir y volver por lo obvio. La que vale es la de la API.
    validar: (v) => ({
      // El mensaje dice qué escribir según la clase, como en el Figma de errores.
      ...(!v.name.trim() && {
        name: v.kind === 'company' ? 'cliente.nombre_empresa' : 'cliente.nombre_persona',
      }),
      ...(editando && !v.code.trim() && { code: 'validation.required' }),
      // El documento va en pareja: un tipo sin número, o un número sin tipo, no
      // hay con qué compararlo. Es la misma regla del validador de la API.
      ...(v.docTypeId && !v.docIdentifier.trim() && { docIdentifier: 'validation.required' }),
      ...(!v.docTypeId && v.docIdentifier.trim() && { docTypeId: 'validation.required' }),
      ...erroresDeContactos(v.contacts),
    }),
    guardar: async (v) => {
      setExistente(null);
      // En el alta, el propuesto que nadie tocó se manda vacío y lo asigna el
      // servidor al guardar: mandar el número que se mostró haría chocar dos
      // altas simultáneas. Si la persona lo cambió, va lo que escribió.
      const problema = ficha
        ? await cambios.editar(
            String(ficha.id),
            version(ficha.version),
            desactivarAlGuardar.current ? { ...v, isActive: false } : v,
          )
        : await cambios.crear({ ...v, code: v.code.trim() === propuesto ? '' : v.code });
      if (problema?.code === 'customer.documento_tomado' && problema.existingCustomer) {
        setExistente(problema.existingCustomer);
      }
      return problema;
    },
    // Los 409 que son de un campo: se marcan bajo él y no arriba. Los contactos
    // repetidos solo pasan en la edición, y son de la tabla entera.
    camposPorCodigo: {
      'customer.codigo_tomado': 'code',
      'customer.documento_tomado': 'docIdentifier',
      ...(editando && {
        'customer.telefono_repetido': 'contacts',
        'customer.correo_repetido': 'contacts',
      }),
    },
    // «Recargar» en el aviso de cambio simultáneo (#178): vuelve a leer la ficha,
    // que llega con otra versión, y la clave de `Editor` lo arranca de nuevo con
    // los valores que guardó la otra persona.
    recargar: () => void cambios.refrescar(),
  });

  const tiposDoc = useTiposDocumento(form.valores.kind);
  const empresa = form.valores.kind === 'company';

  // Cambiar la clase puede dejar fuera el tipo de documento elegido —un NRC no
  // aplica a una persona—. Se limpia en el momento, con su número, mirando a
  // quién aplicaba en la lista que ya se tenía; esperar a la lista nueva sería
  // un efecto que corrige el estado después de dibujarlo.
  //
  // Tocar el documento apaga «Ya existe el cliente…»: era de otro número, y el error
  // de forma que el campo puede pintar al salir no tiene que traerlo de vuelta.
  //
  // Una persona no tiene nombre comercial (#126): al pasar a Persona se vacía, por
  // la misma razón que el documento. El campo ya no se ve, y la API lo rechazaría.
  const formClase: EstadoFormulario<ValoresCliente> = {
    ...form,
    cambiar: (campo, valor) => {
      form.cambiar(campo, valor);
      if (campo === 'docTypeId' || campo === 'docIdentifier') setExistente(null);
      if (campo !== 'kind') return;
      if (valor !== 'company') form.cambiar('tradeName', '');
      const elegido = tiposDoc.data?.find((td) => String(td.id) === form.valores.docTypeId);
      if (elegido && elegido.appliesTo !== 'both' && elegido.appliesTo !== valor) {
        form.cambiar('docTypeId', '');
        form.cambiar('docIdentifier', '');
      }
    },
  };

  const opcionesDoc = (tiposDoc.data ?? []).map((td) => ({
    valor: String(td.id),
    etiqueta: td.code,
  }));
  // El tipo que ya tenía el cliente aunque el taller lo haya apagado: sin esto, la
  // lista lo mostraría vacío. Si ya no aplica, al guardar lo dice la API bajo el campo.
  if (
    ficha?.docTypeId &&
    ficha.docTypeCode &&
    !opcionesDoc.some((o) => o.valor === String(ficha.docTypeId))
  ) {
    opcionesDoc.push({ valor: String(ficha.docTypeId), etiqueta: ficha.docTypeCode });
  }
  const codigoDoc = opcionesDoc.find((o) => o.valor === form.valores.docTypeId)?.etiqueta ?? '';
  // La forma del número del tipo elegido (#155). Un tipo que el taller apagó no
  // viene en la lista y queda sin máscara: se escribe libre.
  const mascaraDoc = tiposDoc.data?.find((td) => String(td.id) === form.valores.docTypeId)?.mask;

  async function guardado(siguiente: 'volver' | 'otro') {
    const nombre = form.valores.name.trim();
    avisar({
      titulo: editando
        ? t('admin.clientes.aviso.guardado', { nombre })
        : t('admin.clientes.aviso.creado', { nombre }),
    });
    if (siguiente === 'otro') {
      // Se limpia antes de pedir el código: con los datos del recién creado a la
      // vista, un segundo «Guardar» en la espera daría de alta un duplicado.
      form.reiniciar(ALTA);
      setPropuesto('');
      requestAnimationFrame(() => document.getElementById(form.idDe('name'))?.focus());
      // El código anterior ya lo tiene el cliente recién guardado: se pide otro.
      const codigo = await pedirSiguienteCodigo();
      setPropuesto(codigo);
      form.cambiar('code', codigo);
      return;
    }
    volver();
  }

  return (
    <>
      <Formulario
        form={form}
        titulo={editando ? t('admin.clientes.editar.titulo') : t('admin.clientes.nuevo')}
        info={editando ? t('admin.clientes.editar.info') : t('admin.clientes.nuevo.info')}
        // Al editar, la línea del Figma: «CLI-00033 · Creado el … · Última modificación: … por …».
        {...(ficha && { descripcion: encabezado(ficha, idioma, t) })}
        {...(editando && { etiquetaGuardar: t('admin.clientes.formulario.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={(siguiente) => void guardado(siguiente)}
        onCancelar={volver}
        {...(ficha && {
          accionSecundaria: (
            <Boton variante="peligro" onClick={() => setEliminando(true)}>
              {t('admin.clientes.formulario.eliminar')}
            </Boton>
          ),
        })}
      >
        <Seccion titulo={t('admin.clientes.formulario.general')}>
          <Fila>
            <CampoSegmentado
              form={formClase}
              nombre="kind"
              etiqueta={t('admin.clientes.formulario.tipo')}
              // Sin `obligatorio`, como en el Figma: siempre hay una de las dos
              // elegida, así que el asterisco no obligaría a nada.
              info={t('admin.clientes.formulario.tipoAyuda')}
              opciones={[
                { valor: 'person', etiqueta: t('admin.clientes.persona') },
                { valor: 'company', etiqueta: t('admin.clientes.empresa') },
              ]}
            />
            <CampoTexto
              form={form}
              nombre="code"
              etiqueta={t('admin.clientes.formulario.codigo')}
              // En el alta, vacío vale: la API asigna el código (`RegisterCustomerValidator`).
              obligatorio={editando}
              {...(!editando && { info: t('admin.clientes.formulario.codigoAyuda') })}
              ancho="corto"
              autoComplete="off"
              maximo={MAXIMO.codigo}
            />
          </Fila>
          {/* En Empresa, la razón social y al lado el nombre comercial (#126), como
              en el Figma. En Persona, solo el nombre. */}
          <Fila>
            <CampoTexto
              form={form}
              nombre="name"
              etiqueta={
                empresa ? t('admin.clientes.formulario.razonSocial') : t('admin.clientes.formulario.nombre')
              }
              obligatorio
              ancho="largo"
              autoComplete="off"
              maximo={MAXIMO.nombre}
            />
            {empresa && (
              <CampoTexto
                form={form}
                nombre="tradeName"
                etiqueta={t('admin.clientes.formulario.nombreComercial')}
                info={t('admin.clientes.formulario.nombreComercialAyuda')}
                placeholder={t('admin.clientes.formulario.nombreComercialEjemplo')}
                ancho="largo"
                autoComplete="off"
                maximo={MAXIMO.nombre}
              />
            )}
          </Fila>
        </Seccion>

        {/* El documento, en su propia sección, como en el Figma de Agregar y Editar (#126). */}
        <Seccion
          titulo={t('admin.clientes.formulario.identificacion')}
          info={t('admin.clientes.formulario.identificacionAyuda')}
        >
          <Fila>
            <CampoSeleccion
              form={formClase}
              nombre="docTypeId"
              etiqueta={t('admin.clientes.formulario.tipoDocumento')}
              info={t('admin.clientes.formulario.tipoDocumentoAyuda')}
              placeholder={t('admin.clientes.formulario.sinDocumento')}
              opciones={opcionesDoc}
            />
            <CampoDocumento
              form={formClase}
              nombre="docIdentifier"
              etiqueta={t('admin.clientes.formulario.numeroDocumento')}
              info={t('admin.clientes.formulario.numeroDocumentoAyuda')}
              ancho="corto"
              mascara={mascaraDoc}
              maximo={MAXIMO.documento}
            />
          </Fila>
          {existente && form.errores.docIdentifier && (
            <p className="clientes__existente" role="status">
              {t('admin.clientes.error.repetido', {
                tipo: codigoDoc,
                numero: form.valores.docIdentifier.trim(),
                nombre: existente.name,
                codigo: existente.code ?? '—',
              })}{' '}
              <button
                type="button"
                className="tipos__enlace"
                // A la ficha del que ya existe (#178), no a su edición: lo que se
                // quiere es ver de quién es el documento. Reemplaza a esta pantalla
                // en el historial, y la ficha sabe que el listado está detrás solo
                // si lo estaba de esta: venir de otra ficha deja esa en el medio.
                onClick={() =>
                  void navegar(`/clientes/${String(existente.id)}`, {
                    replace: true,
                    state: { desdeListado: Boolean(origen?.desdeListado && !origen.desdeFicha) },
                  })
                }
              >
                {t('admin.clientes.error.verExistente')}
              </button>
            </p>
          )}
        </Seccion>

        {editando ? (
          <Seccion
            titulo={t('admin.clientes.formulario.contacto')}
            info={t('admin.clientes.contacto.ayuda')}
          >
            <TablaContactos form={form} />
          </Seccion>
        ) : (
          <Seccion
            titulo={t('admin.clientes.formulario.contacto')}
            info={t('admin.clientes.formulario.contactoAyuda')}
          >
            <Fila>
              <CampoTelefono
                form={form}
                nombre="phone"
                etiqueta={t('admin.clientes.formulario.telefono')}
                info={t('admin.clientes.formulario.telefonoAyuda')}
                ancho="corto"
                maximo={MAXIMO.telefono}
              />
              <CampoCorreo
                form={form}
                nombre="email"
                etiqueta={t('admin.clientes.formulario.correo')}
                info={t('admin.clientes.formulario.correoAyuda')}
                maximo={MAXIMO.correo}
              />
            </Fila>
          </Seccion>
        )}

        {/* Dirección y notas en una misma fila, como en el Figma. */}
        <Seccion titulo={t('admin.clientes.formulario.direccionYNotas')}>
          <Fila>
            <CampoTexto
              form={form}
              nombre="address"
              etiqueta={t('admin.clientes.formulario.direccion')}
              autoComplete="street-address"
              ancho="largo"
              maximo={MAXIMO.direccion}
            />
            <CampoAreaTexto
              form={form}
              nombre="notes"
              etiqueta={t('admin.clientes.formulario.notas')}
              ancho="largo"
              maximo={MAXIMO.notas}
            />
          </Fila>
        </Seccion>

        {/* Sin «Estado» en el alta: el cliente nace activo, y un interruptor que
            nadie apaga al crear es un campo que no sirve. */}
        {editando && (
          <Seccion titulo={t('admin.clientes.formulario.estado')}>
            <CampoInterruptor
              form={form}
              nombre="isActive"
              etiqueta={t('admin.clientes.activo')}
              info={t('admin.clientes.formulario.activoAyuda')}
            />
          </Seccion>
        )}
      </Formulario>

      <EliminarCliente
        cliente={eliminando ? (ficha ?? null) : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
        // Desactivar recarga la ficha y el formulario arranca de nuevo: lo escrito
        // sin guardar se perdería sin aviso. Con cambios, la ventana pregunta.
        cambiosSinGuardar={
          ficha !== undefined && JSON.stringify(form.valores) !== JSON.stringify(valoresDeFicha(ficha))
        }
        guardarYDesactivar={async () => {
          // El nombre de lo que se guarda, no el de antes: puede haber cambiado.
          const nombre = form.valores.name.trim();
          desactivarAlGuardar.current = true;
          try {
            const guardado = await form.enviar();
            if (guardado) avisar({ titulo: t('admin.clientes.aviso.guardadoYDesactivado', { nombre }) });
            return guardado;
          } finally {
            desactivarAlGuardar.current = false;
          }
        }}
      />
    </>
  );
}

/** La ficha como valores del formulario. La dirección viene aparte, no en los contactos. */
function valoresDeFicha(ficha: FichaCliente): ValoresCliente {
  return {
    ...ALTA,
    kind: ficha.kind,
    code: ficha.code ?? '',
    name: ficha.name,
    tradeName: ficha.tradeName ?? '',
    docTypeId: ficha.docTypeId ? String(ficha.docTypeId) : '',
    docIdentifier: ficha.docIdentifier ?? '',
    address: ficha.address ?? '',
    notes: ficha.notes ?? '',
    isActive: ficha.isActive,
    contacts: ficha.contacts.flatMap((c) =>
      c.kind === 'address'
        ? []
        : [
            {
              id: String(c.id),
              kind: c.kind,
              value: c.value,
              label: c.label ?? '',
              isPrimary: c.isPrimary,
            },
          ],
    ),
  };
}

/**
 * Los teléfonos y correos de la edición: «Contacto» del Figma de Editar. No hay
 * un componente así en `@tallerya/ui`; si Proveedores o Personal lo necesitan,
 * se sube.
 *
 * **Principal es uno por tipo, y siempre hay uno** si hay contactos de ese tipo:
 * encender uno apaga los otros de su tipo, y si se quita o cambia de tipo el
 * principal, lo hereda el primero que quede. Por eso el interruptor no se apaga
 * tocándolo: se mueve encendiendo otro.
 *
 * Los errores que la API devuelve por fila —`contacts[0].value`— se dibujan bajo
 * su fila, y el foco del formulario llega a su control por el mismo `id`. Un
 * error que el formulario no dibuja deja a la persona sin salida (#76).
 */
function TablaContactos({ form }: { form: EstadoFormulario<ValoresCliente> }) {
  const { t } = useTraduccion();
  const filas = form.valores.contacts;
  const errores = form.errores as Record<string, string | undefined>;
  // El «Es demasiado largo» dice cuánto cabe, como en los `Campo*` con `maximo`.
  const largo = traducirError('validation.too_long', t);
  const conMaximo = (texto: string | undefined, maximo: number) =>
    texto === largo ? t('ui.campo.maximo', { maximo }) : texto;
  // El mismo `id` que arma `useFormulario` para `contacts[0].value`.
  const idDe = (i: number, campo: keyof ContactoFila) => `${form.idDe('contacts')}[${i}].${campo}`;

  const guardar = (nuevas: ContactoFila[]) => form.cambiar('contacts', conPrincipal(nuevas));

  const editar = (i: number, cambio: Partial<ContactoFila>) =>
    guardar(filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)));

  function encender(i: number) {
    const tipo = filas[i]?.kind;
    guardar(filas.map((f, j) => (f.kind === tipo ? { ...f, isPrimary: j === i } : f)));
  }

  return (
    <div className="contactos">
      {filas.length > 0 && (
        <ul className="contactos__lista">
          <li className="contactos__fila contactos__encabezado" aria-hidden="true">
            <span>{t('admin.clientes.contacto.tipo')}</span>
            <span>{t('admin.clientes.contacto.valor')}</span>
            <span>{t('admin.clientes.contacto.etiqueta')}</span>
            <span>{t('admin.clientes.contacto.principal')}</span>
            <span />
          </li>
          {filas.map((f, i) => {
            const n = i + 1;
            const error = (campo: keyof ContactoFila) => errores[`contacts[${i}].${campo}`];
            const errorFila =
              conMaximo(error('value'), maximoDe(f.kind)) ??
              error('kind') ??
              conMaximo(error('label'), MAXIMO.etiqueta) ??
              error('id');
            return (
              <li key={f.id ?? `nuevo-${i}`} className="contactos__fila">
                <div className={caja(error('kind'))}>
                  <select
                    id={idDe(i, 'kind')}
                    value={f.kind}
                    aria-label={t('admin.clientes.contacto.tipoDe', { n })}
                    aria-invalid={error('kind') ? true : undefined}
                    // El backend no le cambia el tipo a un contacto: pasa a ser
                    // uno nuevo, y el viejo se quita al guardar.
                    onChange={(e) =>
                      editar(i, {
                        id: null,
                        kind: e.target.value as ContactoFila['kind'],
                        isPrimary: false,
                      })
                    }
                  >
                    <option value="phone">{t('admin.clientes.contacto.telefono')}</option>
                    <option value="email">{t('admin.clientes.contacto.correo')}</option>
                  </select>
                  <IconoChevronAbajo />
                </div>
                <div className={caja(error('value'))}>
                  <input
                    id={idDe(i, 'value')}
                    type={f.kind === 'phone' ? 'tel' : 'email'}
                    inputMode={f.kind === 'phone' ? 'tel' : 'email'}
                    autoComplete="off"
                    value={f.value}
                    aria-label={t('admin.clientes.contacto.valorDe', { n })}
                    aria-invalid={error('value') ? true : undefined}
                    // En un teléfono, la letra no entra: la misma regla de `CampoTelefono`.
                    onChange={(e) =>
                      editar(i, { value: f.kind === 'phone' ? guiarTelefono(f.value, e.target.value) : e.target.value })
                    }
                  />
                </div>
                <div className={caja(error('label'))}>
                  <input
                    id={idDe(i, 'label')}
                    value={f.label}
                    placeholder={t('admin.clientes.contacto.etiquetaEjemplo')}
                    aria-label={t('admin.clientes.contacto.etiquetaDe', { n })}
                    aria-invalid={error('label') ? true : undefined}
                    onChange={(e) => editar(i, { label: e.target.value })}
                  />
                </div>
                <label className="ui-interruptor contactos__principal">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={f.isPrimary}
                    aria-label={t('admin.clientes.contacto.principalDe', { n })}
                    onChange={(e) => e.target.checked && encender(i)}
                  />
                  <span className="ui-interruptor__pista" aria-hidden="true" />
                  <span className="contactos__principal-texto" aria-hidden="true">
                    {t('admin.clientes.contacto.principal')}
                  </span>
                </label>
                <Boton
                  variante="texto"
                  soloIcono
                  aria-label={t('admin.clientes.contacto.quitar', { n })}
                  onClick={() => guardar(filas.filter((_, j) => j !== i))}
                >
                  ×
                </Boton>
                {errorFila && <p className="contactos__error">{errorFila}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {errores['contacts'] && <p className="contactos__error">{errores['contacts']}</p>}
      <div>
        {/* El `id` de la lista: así su error general cuenta como visible aunque no
            haya filas, y el foco llega aquí, justo bajo el mensaje (#76). */}
        <Boton
          id={form.idDe('contacts')}
          variante="secundario"
          icono={<IconoMas />}
          onClick={() =>
            guardar([...filas, { id: null, kind: 'phone', value: '', label: '', isPrimary: false }])
          }
        >
          {t('admin.clientes.contacto.agregar')}
        </Boton>
      </div>
    </div>
  );
}

/**
 * La forma de cada contacto (#155), con la clave que usa la API —`contacts[1].value`—
 * para que el error caiga en su fila. Las filas no son `Campo*` y no registran su
 * regla: se revisan acá, al guardar. Una fila vacía la rechaza la API con su propio
 * código, como antes. El tope (#176), igual, al guardar: la fila no lleva contador
 * ni `maxLength`, que cortaría en silencio lo que se pega.
 */
function erroresDeContactos(filas: ContactoFila[]): ErroresValidacion<ValoresCliente> {
  const errores: Record<string, string> = {};
  filas.forEach((f, i) => {
    if (f.label.trim().length > MAXIMO.etiqueta) errores[`contacts[${i}].label`] = 'validation.too_long';
    if (!f.value.trim()) return;
    if (f.kind === 'phone' && !esTelefono(f.value)) errores[`contacts[${i}].value`] = 'validation.phone';
    if (f.kind === 'email' && !esCorreo(f.value)) errores[`contacts[${i}].value`] = 'validation.email';
    if (f.value.trim().length > maximoDe(f.kind)) errores[`contacts[${i}].value`] = 'validation.too_long';
  });
  // Las claves de fila no son campos de `ValoresCliente`, pero el formulario las
  // dibuja igual: son las mismas que trae un 400 de la API.
  return errores as ErroresValidacion<ValoresCliente>;
}

/** La caja de `@tallerya/ui` alrededor de un control de la fila, en rojo si tiene error. */
const caja = (error: string | undefined) =>
  `ui-campo__caja${error ? ' contactos__caja--error' : ''}`;

/** Cada tipo con contactos queda con un principal: el que ya lo era, o el primero de su tipo. */
function conPrincipal(filas: ContactoFila[]): ContactoFila[] {
  const tienen = new Set(filas.filter((f) => f.isPrimary).map((f) => f.kind));
  return filas.map((f) => {
    if (tienen.has(f.kind)) return f;
    tienen.add(f.kind);
    return { ...f, isPrimary: true };
  });
}
